import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Share2 } from 'lucide-react'
import { useLocale } from '../../i18n/context'
import { useSwipeNavBlock } from '../../hooks/useSwipeNavBlock'
import { renderMilestoneShareImage } from '../../utils/shareImage'
import type { MilestoneShareField, MilestoneShareLabels } from '../../utils/shareImage'

interface MilestoneSharePreviewSheetProps {
  isOpen: boolean
  onClose: () => void
  /** Static (locale-resolved) content for the card — everything except the field. */
  labels: MilestoneShareLabels | null
  /** Preselected field, derived from the app's current UI theme. */
  defaultField: MilestoneShareField
  /** Hands the exact previewed blob to the caller's native-share flow. */
  onShare: (blob: Blob, field: MilestoneShareField) => void
  isSharing: boolean
  shareStatus: string | null
}

/**
 * Preview sheet opened by "Share milestone": renders the Direction C card client-side,
 * lets the user flip between the light and inverted field, and hands the exact
 * previewed blob to onShare — one render path for both preview and the shared file.
 * Interaction pattern mirrors MistakesReviewSheet (swipe-down / backdrop / Escape).
 */
export default function MilestoneSharePreviewSheet({
  isOpen, onClose, labels, defaultField, onShare, isSharing, shareStatus,
}: MilestoneSharePreviewSheetProps) {
  const { tr } = useLocale()
  const shouldReduceMotion = useReducedMotion()
  const [field, setField] = useState<MilestoneShareField>(defaultField)
  // Both fields are rendered once on open and cached here — toggling is then a cache
  // swap, never a re-render. Keyed by field so either variant can finish first.
  const [variants, setVariants] = useState<Record<MilestoneShareField, { blob: Blob; url: string } | null>>({
    light: null, inv: null,
  })
  const objectUrlsRef = useRef<string[]>([])
  // ADR-106 P3: blocks while open.
  useSwipeNavBlock(isOpen, 'milestone-share-sheet')

  // Reset local state when the sheet transitions open/closed. Adjusting state during
  // render (not inside an effect) so React can bail out same-render — see
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen)
    if (isOpen) {
      setField(defaultField)
    } else {
      setVariants({ light: null, inv: null })
    }
  }
  const previewUrl = variants[field]?.url ?? null
  const previewBlob = variants[field]?.blob ?? null
  const isRendering = isOpen && !previewUrl

  // Render both fields concurrently on open — a fresh open always re-renders (labels or
  // the app theme may have changed since the sheet last closed). Callers must have paid
  // the canvas font cold-start cost already (see warmShareImageFonts in LearningComplete)
  // so each of these renders is a few ms, not seconds.
  useEffect(() => {
    if (!isOpen || !labels) return
    let cancelled = false
    const fields: MilestoneShareField[] = ['light', 'inv']
    Promise.all(fields.map((f) => renderMilestoneShareImage(labels, f)))
      .then((blobs) => {
        if (cancelled) return
        objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
        const urls = blobs.map((blob) => URL.createObjectURL(blob))
        objectUrlsRef.current = urls
        setVariants({
          light: { blob: blobs[0], url: urls[0] },
          inv: { blob: blobs[1], url: urls[1] },
        })
      })
      .catch(() => {
        // Preview render failure must not break the sheet — keep whatever preview exists.
      })
    return () => { cancelled = true }
  }, [isOpen, labels])

  // Release the cached object URLs once the sheet closes (browser API cleanup, not state).
  useEffect(() => {
    if (isOpen) return
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
    objectUrlsRef.current = []
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  useEffect(() => () => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
  }, [])

  const duration = shouldReduceMotion ? 0 : 0.22
  const m = tr.complete.milestone

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="milestone-share-backdrop"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.28)', zIndex: 90 }}
          />

          <motion.div
            key="milestone-share-sheet"
            role="dialog"
            aria-label={m.previewTitle}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.3}
            onDragEnd={(_, info) => {
              if (info.offset.y > 80 || info.velocity.y > 500) onClose()
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'tween', ease: [0.32, 0.72, 0, 1], duration }}
            style={{
              position: 'fixed', left: 0, right: 0, bottom: 0,
              maxHeight: '88dvh',
              background: 'var(--bg-surface)',
              borderTopLeftRadius: 20, borderTopRightRadius: 20,
              boxShadow: 'var(--shadow-float)',
              borderTop: '1px solid var(--border)',
              zIndex: 91,
              display: 'flex', flexDirection: 'column',
              touchAction: 'pan-y',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px', flexShrink: 0 }}>
              <div style={{ width: 40, height: 4, borderRadius: 50, background: 'var(--border)' }} />
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '4px 20px 10px', flexShrink: 0,
            }}>
              <span style={{
                fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-secondary)',
                textTransform: 'uppercase', letterSpacing: '0.1em',
              }}>
                {m.previewTitle}
              </span>
              <button
                onClick={onClose}
                aria-label={m.previewClose}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-secondary)', padding: 6, borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 32, minHeight: 32,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', padding: '4px 20px 24px' }}>
              <div style={{
                position: 'relative', width: '100%', maxWidth: 280, margin: '0 auto',
                aspectRatio: '1080 / 1350', borderRadius: 16, overflow: 'hidden',
                background: 'var(--bg-secondary)', border: '1px solid var(--border)',
              }}>
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                {(['light', 'inv'] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setField(f)}
                    style={{
                      padding: '8px 20px', borderRadius: 50,
                      border: `1px solid ${field === f ? 'var(--accent)' : 'var(--border)'}`,
                      background: field === f ? 'var(--accent-soft)' : 'transparent',
                      color: field === f ? 'var(--accent)' : 'var(--text-secondary)',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {f === 'light' ? m.fieldLight : m.fieldInverted}
                  </button>
                ))}
              </div>

              <button
                onClick={() => previewBlob && onShare(previewBlob, field)}
                disabled={isSharing || isRendering || !previewBlob}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  width: '100%', marginTop: 20,
                  padding: '12px 40px', borderRadius: 50,
                  border: '1px solid var(--accent)', background: 'var(--accent-soft)',
                  color: 'var(--accent)', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                  opacity: (isSharing || isRendering || !previewBlob) ? 0.6 : 1,
                }}
              >
                <Share2 size={16} />
                {m.shareLabel}
              </button>
              {shareStatus && (
                <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center' }}>
                  {shareStatus}
                </p>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
